import { DEALER_ROLE_IDS, LHPH_ROLE_IDS, UserRoleId } from './enums/UserRoleId.enum';
import { User } from './types/User.type';

export function hasOwnProperties<X, Y extends PropertyKey>(
  obj: X,
  ...props: Y[]
): obj is X & Record<Y, unknown> {
  return props.every(prop => Object.prototype.hasOwnProperty.call(obj, prop));
}

export function sample(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1) + min);
}

export function isString(x: unknown): x is string {
  return typeof x === 'string';
}

export function isDealerMember(user: User | null): boolean {
  if (!user || !user.role_id) {
    return false;
  }

  return DEALER_ROLE_IDS.has(user.role_id);
}

/**
 * May this login run the LHPH staff actions — billing, funding, payoff reversal, swap
 * approvals? (lhphcapital-fdjb, Kevin's decision of 2026-10-07.)
 *
 * It mirrors the API, which refuses a dealer login on those routes (`requireEveryCompany`):
 * everyone who is not a dealer. This only decides what is OFFERED; the refusal is the
 * server's. A login with no role is offered nothing. Number(): a role_id that arrives as a
 * string would otherwise miss the set and read as staff (lhphcapital-3oyq).
 */
export function canRunStaffActions(user: User | null | undefined): boolean {
  if (!user || !user.role_id) {
    return false;
  }

  return !DEALER_ROLE_IDS.has(Number(user.role_id));
}

export function isNotNull<T>(x: T | null): x is T {
  return x !== null;
}

// User role checks
export function isDealerAdmin(user: User | null): boolean {
  if (!user || !user.role_id) {
    return false;
  }

  return user.role_id === UserRoleId.DealerAdmin;
}

export function isLHPHMember(user: User | null): boolean {
  if (!user || !user.role_id) {
    return false;
  }

  return LHPH_ROLE_IDS.has(user.role_id);
}

export function isLHPHAdmin(user: User | null): boolean {
  if (!user || !user.role_id) {
    return false;
  }

  return user.role_id === UserRoleId.LHPHAdmin;
}

export function isSuperAdmin(user: User | null): boolean {
  if (!user || !user.role_id) {
    return false;
  }

  return user.role_id === UserRoleId.SuperAdmin;
}

export function isOrgAdmin(user: User | null): boolean {
  if (!user || !user.role_id) {
    return false;
  }

  // SuperAdmin, Admin, and Dealer Admin get admin access
  return (
    user.role_id === UserRoleId.SuperAdmin ||
    user.role_id === UserRoleId.Admin ||
    user.role_id === UserRoleId.DealerAdmin
  );
}

export function canAccessUsersMenu(user: User | null): boolean {
  if (!user || !user.role_id) {
    return false;
  }

  // SuperAdmin and Admin can always access users menu
  if (user.role_id === UserRoleId.SuperAdmin || user.role_id === UserRoleId.Admin) {
    return true;
  }

  // Only LHPH Admin can access users menu (not LHPH User)
  if (user.role_id === UserRoleId.LHPHAdmin) {
    return true;
  }

  // Dealer Admin can access users menu
  if (user.role_id === UserRoleId.DealerAdmin) {
    return true;
  }

  // Dealer User cannot access users menu
  return false;
}
